import React, { useState, useEffect } from "react";
import { useStore } from "../../store/store";
import { useRequiredMsgs } from "../../store/requiredMsgs";
import { getStileImageLabels, getStilesImage } from "./findImages";
import { pivotLimits } from "./panelWidthLimitations";
import fractionToInt from "../../util/fractionToInt";
import { calculatePanelWidth } from "../../util/formulas/calculatePanelWidth";
import singleDirectionOdds from "../../util/singleDirectionOdds";
import { handlePull } from "../PivotHandle/defaults";

interface UpdateObj {
  lockRail: string;
  stileCategory: string;
  liteConfig?: string;
  stilesAndRails?: string;
  shootBoltColor?: string;
  handleStyle?: string;
  handleColor?: string;
  stickingStops?: string;
  hingeColor?: string;
  secondaryHandle?: string;
}

function useStilesRailsSelector(evenConfig: boolean) {
  const addToQuote = useStore((state) => state.addToQuote);

  const doorType = useStore((state) => state.quote.doorType);
  const isSwing = doorType === "Swing";
  const isPivot = doorType === "Pivot";
  const isSlider = doorType === "Slider";
  const isBifoldWindow = doorType === "Bi-fold Window";
  const isFixedPanel = doorType === "Fixed Panel";
  const isBifold = doorType?.includes("Bi-fold");

  const bifoldConfiguration = useStore((state) => state.quote.configuration);
  const chosenConfig = useStore((state) => state.quote.exactConfig);

  const comboWindowConfig = useStore((state) => state.quote.comboWindowConfig);

  const sliderPocket = useStore((state) => state.quote.sliderPocket);

  const pivotHardware = useStore((state) => state.quote.pivotHardware);
  const pivotAction = useStore((state) => state.quote.pivotAction);

  const swingPanelWidth = useStore((state) => state.quote.swingPanelWidth);
  const swingPanelFraction = useStore(
    (state) => state.quote.swingPanelFraction,
  );

  const addDL = useStore((state) => state.quote.addDL);

  const overallPW = !swingPanelWidth
    ? 0
    : fractionToInt(swingPanelWidth, swingPanelFraction);

  const woodSpecies = useStore((state) => state.quote.woodSpecies);

  const accoyaChosen = woodSpecies?.includes("Accoya");

  const material = useStore((state) => state.quote.material);

  const materialChoice = material.includes("Clad")
    ? "clad"
    : material === "All Wood"
      ? "wood"
      : "";

  const hingeColor = useStore((state) => state.quote.hingeColor);
  const shootBoltColor = useStore((state) => state.quote.shootBoltColor);

  const withHandle = useStore((state) => state.quote.withHandle);
  const handleStyle = useStore((state) => state.quote.handleStyle);
  const handleColor = useStore((state) => state.quote.handleColor);
  const requireTradLockStile =
    withHandle === "Add Handle Set" && handleStyle === "Smart Lock *"
      ? true
      : isPivot && handleStyle === "Smart Lock *"
        ? true
        : doorType === "Pivot" &&
            handlePull.some((itm) => itm.label === handleStyle)
          ? true
          : false;

  const stickingStops = useStore((state) => state.quote.stickingStops);

  const stileCategory = useStore((state) => state.quote.stileCategory);
  const stiles = useStore((state) => state.quote.stilesAndRails);

  const height = useStore((state) => state.quote.height);
  const heightFraction = useStore((state) => state.quote.heightFraction);

  const unitSize = useStore((state) => state.quote.unitSize);
  const unitSizeFraction = useStore((state) => state.quote.unitSizeFraction);

  const overallWidth = fractionToInt(unitSize, unitSizeFraction);

  const overallHeight = fractionToInt(height, heightFraction);

  const findRequired = useRequiredMsgs((state) => state.findRequired);
  const removeRequired = useRequiredMsgs((state) => state.removeRequired);
  const windowShootBoltHeight = useStore(
    (state) => state.quote.windowShootBoltHeight,
  );

  const [panelErr, setPanelErr] = useState<{
    slimline?: string;
    euro?: string;
    euroTrad?: string;
  }>({ slimline: "" });

  const { dsi, dsi2, dsi3 } = getStilesImage({
    stiles: stiles || "",
    panelErr,
    material,
    isAccoya: accoyaChosen,
    doorType,
    evenConfig,
    config: isPivot && pivotHardware ? pivotHardware : chosenConfig,
    pivotMovement: isPivot && pivotAction ? pivotAction : "",
    width: !isSlider ? 0 : overallWidth,
    height: !height || !isSlider ? 0 : fractionToInt(height, heightFraction),
    exactConfig: chosenConfig,
    comboWindowConfig: comboWindowConfig || "",
    requireTradLockStile,
  });

  const { label1, label2, label3 } = getStileImageLabels(
    doorType,
    stiles || "",
    isSlider || chosenConfig === "Fixed Slider",
    isPivot && pivotHardware ? pivotHardware : "",
    isPivot && pivotAction ? pivotAction : "",
    evenConfig,
    requireTradLockStile,
  );

  const showDisclaimerStiles = stileCategory === "Traditional" && !isSlider;

  const panelWidthSlimline =
    materialChoice !== "wood"
      ? calculatePanelWidth({
          doorType: "Slider",
          netWidth: overallWidth,
          numberOfPanels: 0,
          material,
          exactConfig: chosenConfig,
          stilesAndRails: "Slimline",
          sliderPocket,
        })
      : 0;

  useEffect(() => {
    if (!isSlider) return;

    if (panelWidthSlimline && panelWidthSlimline > 60) {
      setPanelErr({
        slimline:
          "Slimline Stiles not available for this Width and Configuration Combination",
      });
    } else {
      setPanelErr({
        slimline: "",
      });
    }
  }, [panelWidthSlimline]);

  useEffect(() => {
    if (!isPivot) return;

    pivotLimits(
      overallPW,
      setPanelErr,
      resetStiles,
      pivotHardware || "",
      pivotAction || "",
      stiles || "",
    );
  }, [overallPW, pivotHardware, pivotAction]);

  const resetStiles = () => {
    addToQuote({
      stilesAndRails: "",
      stileCategory: "",
    });
  };

  const showSlimline = !stileCategory?.includes("Slimline")
    ? false
    : doorType === "Bi-fold" && evenConfig
      ? true
      : isBifoldWindow &&
          bifoldConfiguration !== "3L OR 3R" &&
          bifoldConfiguration !== "5L OR 5R"
        ? true
        : doorType === "Bi-fold Zero Corner" && evenConfig
          ? true
          : doorType === "Bi-fold Door/Window Combo" &&
              evenConfig &&
              !["3L", "3R", "5L", "5R"].includes(comboWindowConfig || "")
            ? true
            : isFixedPanel && chosenConfig !== "Fixed Slider"
              ? true
              : false;

  const willDisplaySlimlineOptions =
    doorType === "Bi-fold" && evenConfig
      ? true
      : isBifoldWindow &&
          bifoldConfiguration !== "3L OR 3R" &&
          bifoldConfiguration !== "5L OR 5R"
        ? true
        : doorType === "Bi-fold Zero Corner" && evenConfig
          ? true
          : doorType === "Bi-fold Door/Window Combo" &&
              evenConfig &&
              !["3L", "3R", "5L", "5R"].includes(comboWindowConfig || "")
            ? true
            : isFixedPanel && chosenConfig !== "Fixed Slider"
              ? true
              : false;

  const showTradBtms = stileCategory === "Traditional";

  const showLockRail =
    !stiles || !stiles?.includes("Slimline")
      ? false
      : materialChoice === "clad" && !panelErr.slimline
        ? true
        : !showSlimline && !panelErr.slimline
          ? true
          : false;

  const handleSlimline = (checked: boolean, type: string) => {
    if (!checked) {
      addToQuote(
        {
          [type]: "",
          lockRail: "",
          stileCategory: "",
        },
        type,
      );
      return;
    }

    let val = "";
    let windowShootBoltHeight: string | null = null;

    if (!willDisplaySlimlineOptions && findRequired("Stiles and Rails"))
      removeRequired("Stiles and Rails");
    if (!willDisplaySlimlineOptions) {
      val = "Slimline";
    }
    let lock = "";
    if (!isSlider) {
      //if options are displayed set lock to "" temporarily until the user clicks the second level option
      lock = !willDisplaySlimlineOptions
        ? '2-1/2" Stiles and Rails with 3-7/16" Lock Stile'
        : "";
    } else {
      lock =
        '2-1/2" Stiles and Top Rails with 3-7/16" Lock Stile and Bottom Rails';
    }

    if (doorType === "Bi-fold Window" || doorType.includes("Combo")) {
      windowShootBoltHeight = "16";
    }

    addToQuote(
      {
        [type]: val,
        lockRail: lock,
        stileCategory: "Slimline",
        windowShootBoltHeight: windowShootBoltHeight,
      },
      type,
    );
  };

  const clearStiles = (type: string) => {
    addToQuote(
      {
        [type]: "",
        stileCategory: "",
      },
      type,
    );
  };

  const clearHandle = (updateObj: { [key: string]: string } | UpdateObj) => {
    const hasForbiddenHandles =
      withHandle === "Add Handle Set" &&
      (handleStyle === "Toulon" || handleStyle === "Savannah");

    if (hasForbiddenHandles && !singleDirectionOdds.includes(chosenConfig)) {
      updateObj["handleStyle"] = "";
      updateObj["handleColor"] = "";
    }
  };

  const handleStilesClick1stLevel = (label: string, type: string) => {
    const checked =
      label.split(" ")[0] === stileCategory ||
      label.split(" ")[0] === stileCategory?.split(" ")[0];
    if (checked) {
      console.log("Clicked Checked");
      if (label.includes("Slimline")) {
        handleSlimline(false, type);
      } else if (label.includes("Euro")) {
        clearStiles(type);
      } else if (label.includes("Traditional")) {
        clearStiles(type);
      } else if (label.includes("Pico")) {
        clearStiles(type);

        let update = {};

        if (shootBoltColor) {
          update["shootBoltColor"] =
            shootBoltColor === "Stainless Steel"
              ? "Brushed Chrome"
              : shootBoltColor;
        }

        if (hingeColor) {
          update["hingeColor"] = hingeColor === "Black" ? "Bronze" : hingeColor;
        }

        if (windowShootBoltHeight) {
          update["windowShootBoltHeight"] = null;
        }

        if (Object.keys(update).length) addToQuote(update);
      }
    } else {
      if (label.includes("Slimline")) {
        handleSlimline(true, type);
        if (isPivot && addDL === "Add Intermediate Rail") {
          addToQuote({ liteConfig: '2 1/2"' });
        }

        if (stileCategory === "Pico") {
          let updateObj = {
            hingeColor: "",
            shootBoltColor: "",
            secondaryHandle: ""
          };

          if (shootBoltColor === "Stainless Steel") {
            updateObj["shootBoltColor"] = "Brushed Chrome";
          } else if (shootBoltColor === "Black") {
            updateObj["shootBoltColor"] = shootBoltColor;
          }

          if (hingeColor === "Stainless Steel") {
            updateObj["hingeColor"] = hingeColor;
          } else if (hingeColor === "Black") {
            updateObj["hingeColor"] = "Bronze";
          }

          if(doorType === "Swing"){
            updateObj["squareHinges"] = ""
            updateObj["hingeColor"] = ""
          }

          if (doorType === "Bi-fold Window" || doorType.includes("Combo")) {
            updateObj["windowShootBoltHeight"] = "16";
          }

          clearHandle(updateObj);
          addToQuote(updateObj);
        }
      } else if (label.includes("Euro")) {
        if (findRequired("Stiles and Rails"))
          removeRequired("Stiles and Rails");

        let updateObj: UpdateObj = {
          [type]: label,
          lockRail: "",
          stileCategory: "Euro",
          secondaryHandle: ""
        };

        if (isPivot && addDL === "Add Intermediate Rail") {
          updateObj["liteConfig"] = '2 1/2"';
        }

        if (stileCategory === "Pico") {
          if (shootBoltColor === "Stainless Steel") {
            updateObj["shootBoltColor"] = "Brushed Chrome";
          } else if (shootBoltColor === "Black") {
            updateObj["shootBoltColor"] = shootBoltColor;
          } else {
            updateObj["shootBoltColor"] = "";
          }

          if (hingeColor === "Stainless Steel") {
            updateObj["hingeColor"] = hingeColor;
          } else if (hingeColor === "Black") {
            updateObj["hingeColor"] = "Bronze";
          }

          if(doorType === "Swing"){
            updateObj["squareHinges"] = ""
            updateObj["hingeColor"] = ""
          }

          clearHandle(updateObj);
        }

        if (doorType === "Bi-fold Window" || doorType.includes("Combo")) {
          updateObj["windowShootBoltHeight"] = "16";
        }

        addToQuote(updateObj, "stilesAndRails");
      } else if (label.includes("Traditional")) {
        let updateObj: UpdateObj = {
          stilesAndRails: "",
          lockRail: "",
          stileCategory: "Traditional",
          secondaryHandle: ""
        };

        if (isPivot && addDL === "Add Intermediate Rail") {
          updateObj["liteConfig"] = '5 1/4"';
        }

        if (stileCategory === "Pico") {
          if (shootBoltColor === "Stainless Steel") {
            updateObj["shootBoltColor"] = "Brushed Chrome";
          } else if (shootBoltColor === "Black") {
            updateObj["shootBoltColor"] = shootBoltColor;
          } else {
            updateObj["shootBoltColor"] = "";
          }

          if (hingeColor === "Stainless Steel") {
            updateObj["hingeColor"] = hingeColor;
          } else if (hingeColor === "Black") {
            updateObj["hingeColor"] = "Bronze";
          }

          if(doorType === "Swing"){
            updateObj["squareHinges"] = ""
            updateObj["hingeColor"] = ""
          }

          clearHandle(updateObj);
        }

        if (doorType === "Bi-fold Window" || doorType.includes("Combo")) {
          updateObj["windowShootBoltHeight"] = "16";
        }

        addToQuote(updateObj, "stilesAndRails");
      } else if (label.includes("Pico")) {
        if (findRequired("Stiles and Rails"))
          removeRequired("Stiles and Rails");

        let updateObj: UpdateObj = {
          [type]: label,
          lockRail: "",
          stileCategory: "Pico",
          shootBoltColor: "",
          hingeColor: "",
        };

        if (shootBoltColor === "Brushed Chrome") {
          updateObj["shootBoltColor"] = "Stainless Steel";
        } else if (shootBoltColor === "Black") {
          updateObj["shootBoltColor"] = shootBoltColor;
        }

        if (hingeColor === "Stainless Steel") {
          updateObj["hingeColor"] = hingeColor;
        } else if (hingeColor === "Bronze") {
          updateObj["hingeColor"] = "Black";
        }

        if (handleStyle !== "Savannah" && handleStyle !== "Toulon") {
          //special order not allowed on pico stile
          updateObj["handleStyle"] = "";
          updateObj["handleColor"] = "";
        }

        if (
          (handleStyle === "Savannah" || handleStyle === "Toulon") &&
          handleColor === "Rustic Umber"
        ) {
          updateObj["handleColor"] = "";
        }

        if (stickingStops === "Ovolo") {
          updateObj["stickingStops"] = "";
        }

        if (doorType === "Bi-fold Window" || doorType.includes("Combo")) {
          updateObj["windowShootBoltHeight"] = "19";
        }

        if(doorType === "Swing"){
          updateObj["hingeColor"] = "Black";
          updateObj["squareHinges"] = "Concealed Hinges";
          removeRequired("Hinge Color");
        }

        addToQuote(updateObj, "stilesAndRails");
      }
    }
  };

  const handleStilesClick2ndLevel = (label: string, type: string) => {
    const checked = label === stiles;

    if (checked) {
      if (label.includes("Slimline")) {
        handleSlimline(false, type);
      } else if (label.includes("Euro")) {
        clearStiles(type);
      } else if (label.includes("Traditional")) {
        clearStiles(type);
      }
    } else {
      if (label.includes("Slimline")) {
        let category = !label.includes("Traditional")
          ? "Slimline"
          : "Slimline Traditional";
        let rail = !label.includes("Traditional") ? '2-1/2"' : '4-3/4"';

        if (findRequired("Stiles and Rails")) {
          removeRequired("Stiles and Rails");
        }

        addToQuote({
          [type]: label,
          stileCategory: category,
          lockRail: `2-1/2" Stiles and Rails with ${rail} Lock Stile`,
        });
      } else if (label.includes("Bottom")) {
        if (findRequired("Stiles and Rails")) {
          removeRequired("Stiles and Rails");
        }

        addToQuote({ [type]: label }, `${type}`);
      }
    }
  };

  const [showLabels, setShowLabels] = useState(false);

  const showImageLabels = () => setShowLabels(true);

  return {
    stiles,
    stileCategory,
    dsi,
    dsi2,
    dsi3,
    label1,
    label2,
    label3,
    showDisclaimerStiles,
    showTradBtms,
    showLockRail,
    handleStilesClick1stLevel,
    handleStilesClick2ndLevel,
    showLabels,
    showImageLabels,
    materialChoice,
    doorType,
    panelErr,
    chosenConfig,
    showSlimline,
    bifoldConfiguration,
    accoyaChosen,
    overallHeight,
    isSwing,
    isPivot,
    isSlider,
    isBifold,
    isBifoldWindow,
    isFixedPanel,
  };
}

export default useStilesRailsSelector;
